import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSharedFile } from '../contexts/SharedFileContext';
import { Button } from './ui/button';
import { 
  LayoutDashboard, 
  FolderKanban, 
  Users, 
  Building2, 
  Link2, 
  Receipt, 
  Settings, 
  LogOut,
  Menu,
  X,
  Bell
} from 'lucide-react';

const Layout = ({ children }) => {
  const { user, logout, settings } = useAuth();
  const { sharedFile } = useSharedFile();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const shareRedirected = useRef(false);

  // Intent Queue: redirect AFTER auth is resolved AND sharedFile is delivered.
  // Runs regardless of current path, but guards against the target page itself
  // (no loop) and against re-firing via shareRedirected ref.
  useEffect(() => {
    if (!user || !sharedFile || shareRedirected.current) return;

    const target = user.role === 'administrator'
      ? '/admin/providers?share=1'
      : user.role === 'client'
        ? '/admin/financial-launches?share=1'
        : null;

    if (!target) return; // provider role: not allowed to receive share

    const targetPath = target.split('?')[0];
    if (location.pathname === targetPath) return;

    shareRedirected.current = true;
    navigate(target, { replace: true });
  }, [user, sharedFile, navigate, location.pathname]);

  // Reset flag when shared file is cleared so future shares redirect again
  useEffect(() => {
    if (!sharedFile) shareRedirected.current = false;
  }, [sharedFile]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNavigation = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const adminNavItems = [
    { path: '/admin', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/admin/projects', icon: FolderKanban, label: 'Projetos' },
    { path: '/admin/providers', icon: Building2, label: 'Prestadores/Fornecedores' },
    { path: '/admin/users', icon: Users, label: 'Usuários' },
    { path: '/admin/cost-centers', icon: Link2, label: 'Centros de Custo' },
    { path: '/admin/financial-launches', icon: Receipt, label: 'Lançamentos' },
    { path: '/admin/notifications', icon: Bell, label: 'Notificações' },
    { path: '/admin/settings', icon: Settings, label: 'Configurações' },
  ];

  const clientNavItems = [
    { path: '/admin', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/admin/projects', icon: FolderKanban, label: 'Projetos' },
    { path: '/admin/financial-launches', icon: Receipt, label: 'Lançamentos' },
  ];

  const providerNavItems = [
    { path: '/admin', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/admin/projects', icon: FolderKanban, label: 'Projetos' },
    { path: '/admin/financial-launches', icon: Receipt, label: 'Lançamentos' },
  ];

  // Select nav items based on user role
  const navItems = user?.role === 'client' ? clientNavItems 
    : user?.role === 'provider' ? providerNavItems 
    : adminNavItems;

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      {/* Header */}
      <header className="backdrop-blur-md bg-white/80 border-b border-slate-200/50 sticky top-0 z-50">
        <div className="px-4 md:px-8 lg:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 -ml-2 text-slate-600 hover:bg-slate-100 rounded-sm"
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            <div>
              <h1 className="text-lg md:text-xl font-secondary font-bold uppercase tracking-tight text-slate-900">
                {settings?.company_name || 'Sistema de Gestão'}
              </h1>
              <p className="text-xs text-slate-600 font-primary hidden sm:block">
                {settings?.subtitle || ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right hidden md:block">
              <p className="text-sm font-medium text-slate-900">{user?.name}</p>
              <p className="text-xs text-slate-600">
                {user?.role === 'administrator' ? 'Administrador' : 
                 user?.role === 'client' ? 'Cliente' : 'Prestador/Fornecedor'}
              </p>
            </div>
            <Button
              onClick={handleLogout}
              variant="ghost"
              size="sm"
              className="hover:bg-slate-100 text-slate-600"
              data-testid="logout-button"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside 
        className={`fixed top-0 left-0 bottom-0 w-64 bg-white border-r border-slate-200 z-50 transform transition-transform duration-200 ease-in-out md:hidden pt-[73px] ${
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* User info on mobile */}
        <div className="p-4 border-b border-slate-100">
          <p className="text-sm font-medium text-slate-900">{user?.name}</p>
          <p className="text-xs text-slate-600">
            {user?.role === 'administrator' ? 'Administrador' : 
             user?.role === 'client' ? 'Cliente' : 'Prestador/Fornecedor'}
          </p>
        </div>
        <nav className="p-4 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                onClick={() => handleNavigation(item.path)}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-sm text-sm font-medium transition-all ${
                  isActive
                    ? 'bg-slate-900 text-white'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <Icon className="h-4 w-4" strokeWidth={1.5} />
                {item.label}
              </button>
            );
          })}
        </nav>
      </aside>

      <div className="flex">
        {/* Desktop Sidebar */}
        <aside className="hidden md:block w-64 border-r border-slate-200 min-h-[calc(100vh-73px)] bg-white">
          <nav className="p-4 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-sm text-sm font-medium transition-all ${
                    isActive
                      ? 'bg-slate-900 text-white'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                  data-testid={`nav-desktop-${item.label.toLowerCase()}`}
                >
                  <Icon className="h-4 w-4" strokeWidth={1.5} />
                  {item.label}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-w-0 p-4 md:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
